import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { getSocket } from '../api/socket';
import OpenRoundForm from './OpenRoundForm';
import { useRoundTimer } from '../hooks/useRoundTimer';
import ResultsByBidder from './ResultsByBidder';

interface Props {
  auctions: any[];
  onRefresh: () => void;
}

// Displayed prices carry thousands separators; a missing price renders as a dash.
// Display only -- CSV exports, the template JSON, and form inputs must stay unformatted.
const fmtPrice = (v: any) => (typeof v === 'number' ? v.toLocaleString() : '—');

function getAuctionRoundStatus(auction: any) {
  if (auction.status === 'CLOSED') return { text: 'Auction Closed', bg: '#fce8e6', color: '#c62828' };
  if (auction.status === 'CLOCK_COMPLETE') return { text: 'Clock Phase Complete', bg: '#fff8e1', color: '#f57f17' };
  const active = auction.rounds?.find((r: any) => r.status === 'ACTIVE');
  if (active) return { text: `Round ${active.roundNumber} Open`, bg: '#e6f4ea', color: '#1e7e34' };
  const lastClosed = [...(auction.rounds ?? [])].reverse().find((r: any) => r.status === 'CLOSED');
  if (lastClosed) return { text: `Round ${lastClosed.roundNumber} Closed`, bg: '#fff3e0', color: '#e65100' };
  return { text: 'Auction Pending', bg: '#e8eaf6', color: '#3949ab' };
}

export default function AuctioneerDashboard({ auctions, onRefresh }: Props) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<any | null>(null);
  const [auctionDetail, setAuctionDetail] = useState<any | null>(null);
  const [bidCount, setBidCount] = useState(0);
  const [showRoundForm, setShowRoundForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [showActivityLog, setShowActivityLog] = useState(true);
  const [showAuctionStatus, setShowAuctionStatus] = useState(true);
  const [showRoundResults, setShowRoundResults] = useState(true);
  const [roundEndTime, setRoundEndTime] = useState<string | null>(null);
  const [cloningAuction, setCloningAuction] = useState<any | null>(null);
  const [cloneTitle, setCloneTitle] = useState('');
  const [cloneRound, setCloneRound] = useState(0);
  const [cloneRho, setCloneRho] = useState('');
  const [clonePriceIncrement, setClonePriceIncrement] = useState('');
  const [cloneActivityTolerance, setCloneActivityTolerance] = useState('');
  const [cloneError, setCloneError] = useState('');
  const [allRoundBids, setAllRoundBids] = useState<Record<string, any[]>>({});
  const [finalResults, setFinalResults] = useState<any | null>(null);
  const { label: timerLabel, timeLeft } = useRoundTimer(roundEndTime);

  // Bidbots state
  const [showBidbots, setShowBidbots] = useState(false);
  const [botEnabled, setBotEnabled] = useState(false);
  const [botArtifacts, setBotArtifacts] = useState<any[]>([]);
  const [botAssignments, setBotAssignments] = useState<any[]>([]);
  const [botExecutions, setBotExecutions] = useState<any[]>([]);
  const [allBidders, setAllBidders] = useState<any[]>([]);
  const [botStrategyFile, setBotStrategyFile] = useState<File | null>(null);
  const [botParamFile, setBotParamFile] = useState<File | null>(null);
  const [botUploadMsg, setBotUploadMsg] = useState('');
  const [botFileKey, setBotFileKey] = useState(0);
  const [showBotExecutions, setShowBotExecutions] = useState(false);
  const [newAssignBidderId, setNewAssignBidderId] = useState('');
  const [newAssignStrategyId, setNewAssignStrategyId] = useState('');
  const [newAssignParamId, setNewAssignParamId] = useState('');
  const [assignError, setAssignError] = useState('');
  const [showArtifactLibrary, setShowArtifactLibrary] = useState(true);
  const [artifactDeleteMsg, setArtifactDeleteMsg] = useState('');
  // Transient feedback for the Auction ID copy button.
  const [copiedId, setCopiedId] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  type LogEntry = { time: string; roundNumber: number } & ({ kind: 'bid'; bidderName: string } | { kind: 'round_opened' | 'round_closed' });
  const [bidLogs, setBidLogs] = useState<Record<string, LogEntry[]>>({});

  const loadAllRoundBids = useCallback(async (rounds: any[]) => {
    const closed = rounds.filter((r: any) => r.status === 'CLOSED');
    if (closed.length === 0) { setAllRoundBids({}); return; }
    try {
      const results = await Promise.all(
        closed.map((r: any) => api.get(`/rounds/${r.id}/bids/all`).then(res => [r.id, res.data] as [string, any[]]))
      );
      setAllRoundBids(Object.fromEntries(results));
    } catch {
      setAllRoundBids({});
    }
  }, []);

  const loadArtifacts = useCallback(async () => {
    try {
      const { data } = await api.get('/bidbot/artifacts');
      setBotArtifacts(data);
    } catch {
      // bidbot not configured — ignore silently
    }
  }, []);

  const loadBotData = useCallback(async (auctionId: string) => {
    try {
      const [artifactsRes, assignmentsRes, executionsRes, biddersRes] = await Promise.all([
        api.get('/bidbot/artifacts'),
        api.get('/bidbot/assignments', { params: { auctionId } }),
        api.get('/bidbot/executions', { params: { auctionId } }),
        api.get('/users/bidders'),
      ]);
      setBotArtifacts(artifactsRes.data);
      setBotAssignments(assignmentsRes.data);
      setBotExecutions(executionsRes.data);
      setAllBidders(biddersRes.data);
    } catch {
      // bidbot not configured — ignore silently
    }
  }, []);

  const loadAuctionDetail = useCallback(async (id: string) => {
    const { data } = await api.get(`/auctions/${id}`);
    setAuctionDetail(data);
    setBotEnabled(data.botEnabled ?? false);
    const active = data.rounds.find((r: any) => r.status === 'ACTIVE');
    setRoundEndTime(active?.endTime ?? null);
    if (active) {
      try {
        const { data: bids } = await api.get(`/rounds/${active.id}/bids/all`);
        setBidCount(bids.length);
      } catch {
        // non-critical
      }
    }
    loadAllRoundBids(data.rounds);
    loadBotData(id);
    if (data.status === 'CLOSED') {
      try {
        const { data: results } = await api.get(`/auctions/${id}/results`);
        setFinalResults(results);
      } catch {
        setFinalResults(null);
      }
    } else {
      setFinalResults(null);
    }
  }, [loadAllRoundBids, loadBotData]);

  useEffect(() => { loadArtifacts(); }, [loadArtifacts]);

  useEffect(() => {
    if (!selected) return;
    setBidCount(0);
    loadAuctionDetail(selected.id);
    const auctionId = selected.id;
    const addEntry = (entry: LogEntry) =>
      setBidLogs(prev => ({ ...prev, [auctionId]: [...(prev[auctionId] ?? []), entry] }));
    const socket = getSocket();
    if (!socket) return;
    socket.emit('join:auction', auctionId);
    socket.on('bid:received', (data: any) => {
      setBidCount(data.bidCount);
      if (data.bidderName) {
        addEntry({ kind: 'bid', time: data.submittedAt ?? new Date().toISOString(), bidderName: data.bidderName, roundNumber: data.roundNumber });
      }
      loadBotData(auctionId);
    });
    socket.on('round:opened', (data: any) => {
      setRoundEndTime(data.endTime ?? null);
      loadAuctionDetail(auctionId);
      addEntry({ kind: 'round_opened', time: new Date().toISOString(), roundNumber: data.roundNumber });
    });
    socket.on('round:closed', (data: any) => {
      setRoundEndTime(null);
      loadAuctionDetail(auctionId);
      setBidLogs(prev => {
        const log = prev[auctionId] ?? [];
        const roundNumber = data.roundNumber ?? (log.length > 0 ? log[log.length - 1].roundNumber : 0);
        return { ...prev, [auctionId]: [...log, { kind: 'round_closed', time: new Date().toISOString(), roundNumber }] };
      });
    });
    socket.on('auction:clock-complete', () => { setRoundEndTime(null); setShowRoundForm(false); loadAuctionDetail(selected.id); onRefresh(); });
    socket.on('auction:closed', () => { setRoundEndTime(null); loadAuctionDetail(selected.id); onRefresh(); });
    return () => {
      socket.emit('leave:auction', selected.id);
      socket.off('bid:received');
      socket.off('round:opened');
      socket.off('round:closed');
      socket.off('auction:clock-complete');
      socket.off('auction:closed');
    };
  }, [selected, loadAuctionDetail, onRefresh]);

  async function closeRound(roundId: string) {
    await api.post(`/auctions/${selected.id}/rounds/${roundId}/close`);
    loadAuctionDetail(selected.id);
  }

  async function processResults() {
    await api.post(`/auctions/${selected.id}/process-results`);
    loadAuctionDetail(selected.id);
  }

  async function deleteAuction(auctionId: string) {
    if (!window.confirm('Delete this auction and all its data? This cannot be undone.')) return;
    await api.delete(`/auctions/${auctionId}`);
    setSelected(null);
    setAuctionDetail(null);
    onRefresh();
  }

  async function toggleAuctionBots(enabled: boolean) {
    if (!selected) return;
    await api.patch(`/bidbot/auctions/${selected.id}/bot-enabled`, { botEnabled: enabled });
    setBotEnabled(enabled);
  }

  async function uploadArtifact(file: File | null, type: string) {
    if (!file) return;
    setBotUploadMsg('');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('artifact_type', type);
    try {
      await api.post('/bidbot/artifacts', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBotUploadMsg(`${type === 'STRATEGY' ? 'Strategy' : 'Parameters'} uploaded successfully.`);
      setBotFileKey(k => k + 1);
      setBotStrategyFile(null);
      setBotParamFile(null);
      loadArtifacts();
      if (selected) loadBotData(selected.id);
    } catch (err: any) {
      setBotUploadMsg(err.response?.data?.detail ?? 'Upload failed');
    }
  }

  async function createAssignment() {
    if (!newAssignBidderId || !newAssignStrategyId || !newAssignParamId || !selected) return;
    setAssignError('');
    try {
      await api.post('/bidbot/assignments', {
        auctionId: selected.id,
        bidderId: newAssignBidderId,
        strategyArtifactId: newAssignStrategyId,
        parameterArtifactId: newAssignParamId,
      });
      setNewAssignBidderId('');
      setNewAssignStrategyId('');
      setNewAssignParamId('');
      loadBotData(selected.id);
    } catch (err: any) {
      setAssignError(err.response?.data?.detail ?? 'Assignment failed');
    }
  }

  async function deleteAssignment(id: string, bidderName: string) {
    if (!window.confirm(`Remove bot assignment for "${bidderName}"? Execution history will also be deleted.`)) return;
    try {
      await api.delete(`/bidbot/assignments/${id}`);
      if (selected) loadBotData(selected.id);
    } catch (err: any) {
      setAssignError(err.response?.data?.detail ?? 'Delete failed');
    }
  }

  async function deleteArtifact(id: string, filename: string) {
    if (!window.confirm(`Delete artifact "${filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/bidbot/artifacts/${id}`);
      setBotArtifacts(prev => prev.filter((a: any) => a.id !== id));
      setArtifactDeleteMsg('');
    } catch (err: any) {
      setArtifactDeleteMsg(err.response?.data?.detail ?? 'Delete failed');
    }
  }

  async function toggleAssignment(id: string, enabled: boolean) {
    await api.patch(`/bidbot/assignments/${id}`, { enabled });
    if (selected) loadBotData(selected.id);
  }

  function exportAuctionTemplate() {
    if (!auctionDetail) return;
    const template = {
      title: auctionDetail.title,
      description: auctionDetail.description ?? '',
      rho: auctionDetail.rho,
      priceIncrement: auctionDetail.priceIncrement,
      activityTolerance: auctionDetail.activityTolerance,
      categories: auctionDetail.categories.map((c: any) => ({
        name: c.name,
        supply: c.supply,
        activityPoints: c.activityPoints,
        startingPrice: c.startingPrice,
        description: c.description ?? '',
      })),
      caps: (auctionDetail.caps ?? []).map((cap: any) => ({
        name: cap.name,
        limit: cap.limit,
        terms: (cap.terms ?? []).map((t: any) => ({
          categoryName: auctionDetail.categories.find((c: any) => c.id === t.categoryId)?.name ?? '',
          coefficient: t.coefficient,
        })),
      })),
    };
    const json = JSON.stringify(template, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${auctionDetail.title.replace(/[^a-z0-9]/gi, '-')}-template.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function triggerDownload(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function getSortedBidders() {
    const bidderMap = new Map<string, string>();
    for (const bids of Object.values(allRoundBids)) {
      for (const bid of bids) bidderMap.set(bid.bidderId, bid.bidderName);
    }
    return Array.from(bidderMap.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { numeric: true })
    );
  }

  function downloadAggregateCsv() {
    if (!auctionDetail) return;
    const cats = auctionDetail.categories;
    const closedRounds = [...auctionDetail.rounds]
      .sort((a: any, b: any) => a.roundNumber - b.roundNumber)
      .filter((r: any) => r.status === 'CLOSED');

    // Rows = rounds, columns: Round | [all prices] | [all demands]
    const header = [
      'Round',
      ...cats.map((c: any) => `${c.name} Price`),
      ...cats.map((c: any) => `${c.name} Demand`),
    ].join(',');
    const rows = closedRounds.map((r: any) => [
      r.roundNumber,
      ...cats.map((c: any) => r.categoryPrices.find((x: any) => x.categoryId === c.id)?.price ?? ''),
      ...cats.map((c: any) => r.categoryPrices.find((x: any) => x.categoryId === c.id)?.aggregateDemand ?? ''),
    ].join(','));

    triggerDownload([header, ...rows].join('\n'), `${auctionDetail.title.replace(/[^a-z0-9]/gi, '-')}-aggregate.csv`);
  }

  function downloadBidderCsv() {
    if (!auctionDetail) return;
    const cats = auctionDetail.categories;
    const closedRounds = [...auctionDetail.rounds]
      .sort((a: any, b: any) => a.roundNumber - b.roundNumber)
      .filter((r: any) => r.status === 'CLOSED');
    const bidders = getSortedBidders();

    // Rows = rounds, columns: Round | [all prices] | [bidder1 per cat] | [bidder2 per cat] | ...
    const header = [
      'Round',
      ...cats.map((c: any) => `${c.name} Price`),
      ...bidders.flatMap(([, name]) => cats.map((c: any) => `${c.name} ${name}`)),
    ].join(',');
    const rows = closedRounds.map((r: any) => [
      r.roundNumber,
      ...cats.map((c: any) => r.categoryPrices.find((x: any) => x.categoryId === c.id)?.price ?? ''),
      ...bidders.flatMap(([bidderId]) =>
        cats.map((c: any) => {
          const bid = (allRoundBids[r.id] ?? []).find((b: any) => b.bidderId === bidderId);
          return bid?.items.find((i: any) => i.categoryId === c.id)?.quantity ?? 0;
        })
      ),
    ].join(','));

    triggerDownload([header, ...rows].join('\n'), `${auctionDetail.title.replace(/[^a-z0-9]/gi, '-')}-bidder.csv`);
  }

  function downloadExitBidCsv() {
    if (!auctionDetail) return;
    const closedRounds = [...auctionDetail.rounds]
      .sort((a: any, b: any) => a.roundNumber - b.roundNumber)
      .filter((r: any) => r.status === 'CLOSED');
    const allExitBids = closedRounds.flatMap((r: any) =>
      (r.exitBidHistory ?? []).map((eb: any) => ({ ...eb, _roundNumber: r.roundNumber }))
    );
    if (allExitBids.length === 0) return;
    const header = ['Round', 'Bidder', 'Category', 'From Qty', 'Additional Qty', 'To Qty', 'Bid Price', 'Kind', 'Active', 'Status'].join(',');
    const rows = allExitBids.map((eb: any) => [
      eb._roundNumber,
      eb.bidderName,
      eb.categoryName,
      eb.fromQuantity,
      eb.incrementQuantity,
      eb.toQuantity,
      eb.bidPrice,
      eb.exitBidKind,
      eb.isActive ? 'Yes' : 'No',
      eb.roundState?.status ?? '',
    ].join(','));
    triggerDownload([header, ...rows].join('\n'), `${auctionDetail.title.replace(/[^a-z0-9]/gi, '-')}-exit-bids.csv`);
  }

  if (selected && auctionDetail) {
    const currentRound = auctionDetail.rounds.find((r: any) => r.status === 'ACTIVE');
    const lastClosedRound = [...auctionDetail.rounds].reverse().find((r: any) => r.status === 'CLOSED');
    const roundBids = allRoundBids[lastClosedRound?.id] ?? [];
    const sortedRounds = [...auctionDetail.rounds].sort((a: any, b: any) => a.roundNumber - b.roundNumber);
    const closedRounds = sortedRounds.filter((r: any) => r.status === 'CLOSED');
    const roundStatus = getAuctionRoundStatus(auctionDetail);

    return (
      <div>
        <div style={{ marginBottom: '1rem' }}>
          <button style={{ ...styles.backBtn, marginBottom: 0 }} onClick={() => { setSelected(null); setAuctionDetail(null); setBotAssignments([]); setBotExecutions([]); setBotEnabled(false); setBotUploadMsg(''); setAssignError(''); }}>← Back</button>
        </div>
        <div style={styles.detailCard}>
          <div style={styles.detailHeader}>
            <h3 style={{ margin: 0 }}>{auctionDetail.title}</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', background: roundStatus.bg, color: roundStatus.color }}>{roundStatus.text}</span>
              <button style={styles.cloneBtn} onClick={() => {
                setCloningAuction({ ...auctionDetail, closedCount: closedRounds.length });
                setCloneTitle(auctionDetail.title + ' (Clone)');
                setCloneRound(0);
                setCloneRho(String(auctionDetail.rho ?? 1));
                setClonePriceIncrement(String(auctionDetail.priceIncrement ?? 0));
                setCloneActivityTolerance(String(auctionDetail.activityTolerance ?? 0));
                setCloneError('');
              }}>Clone</button>
              <button style={styles.deleteBtn} onClick={() => deleteAuction(auctionDetail.id)}>Delete</button>
            </div>
          </div>

          <div style={styles.roundBox}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button style={styles.collapseBtn} onClick={() => setShowAuctionStatus(v => !v)}>
                  <span style={styles.chevron(showAuctionStatus)}>▶</span> Current Auction Details
                </button>
                <button
                  style={(auctionDetail.status === 'ACTIVE' || auctionDetail.status === 'PENDING') && !currentRound ? styles.openBtn : styles.openBtnDisabled}
                  disabled={(auctionDetail.status !== 'ACTIVE' && auctionDetail.status !== 'PENDING') || !!currentRound}
                  title={currentRound ? 'Close the current round before opening a new one' : auctionDetail.status === 'CLOCK_COMPLETE' ? 'Clock phase complete — no new rounds' : undefined}
                  onClick={() => setShowRoundForm(true)}
                >Open Next Round</button>
                <button
                  style={auctionDetail.status === 'CLOCK_COMPLETE' ? styles.processBtn : styles.processBtnDisabled}
                  disabled={auctionDetail.status !== 'CLOCK_COMPLETE'}
                  title={auctionDetail.status === 'ACTIVE' ? 'Clock phase still running — close all rounds first' : undefined}
                  onClick={processResults}
                >Calculate Allocation</button>
                {(() => {
                  const auditActive = auctionDetail.status === 'CLOCK_COMPLETE' || auctionDetail.status === 'CLOSED';
                  return (
                    <button
                      style={auditActive ? styles.auditBtn : styles.auditBtnDisabled}
                      disabled={!auditActive}
                      onClick={() => navigate(`/auctioneer/audit/${selected.id}`)}
                    >Allocation Audit</button>
                  );
                })()}
              </div>
              {currentRound && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.8rem', color: '#555' }}>
                    Round {currentRound.roundNumber}&nbsp;&nbsp;|&nbsp;&nbsp;Bids: <strong>{bidCount}</strong>
                  </span>
                  <button
                    style={{ ...styles.closeBtn, background: timeLeft !== null && timeLeft <= 10 ? '#c62828' : '#e53935', marginTop: 0 }}
                    onClick={() => closeRound(currentRound.id)}
                  >
                    {timerLabel !== 'No time limit' ? `Close in ${timerLabel}` : 'Close'}
                  </button>
                </div>
              )}
            </div>

            {/* Two-column body */}
            {showAuctionStatus && <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'stretch', marginTop: '0.75rem' }}>
              {/* Clock Prices */}
              <div style={{ flex: 3, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>
                    {currentRound
                      ? `Round ${currentRound.roundNumber} Open`
                      : lastClosedRound
                        ? `Round ${lastClosedRound.roundNumber} Closed`
                        : 'Clock Prices'}
                  </div>
                  <button style={styles.csvBtn} onClick={exportAuctionTemplate}>Export Template JSON</button>
                </div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Category</th>
                      <th style={styles.thNum}>Act. Pts</th>
                      <th style={styles.thNum}>Supply</th>
                      <th style={styles.thNum}>Last Demand</th>
                      <th style={styles.thNum}>Last Excess</th>
                      <th style={styles.thNum}>Starting Price</th>
                      <th style={styles.thNum}>Current Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auctionDetail.categories.map((cat: any) => {
                      const priceSource = currentRound ?? lastClosedRound;
                      const p = priceSource?.categoryPrices.find((x: any) => x.categoryId === cat.id);
                      const lastDemand = lastClosedRound?.categoryPrices.find((x: any) => x.categoryId === cat.id)?.aggregateDemand;
                      return (
                        <tr key={cat.id}>
                          <td style={styles.td}>{cat.name}</td>
                          <td style={styles.tdNum}>{cat.activityPoints}</td>
                          <td style={styles.tdNum}>{cat.supply}</td>
                          <td style={{ ...styles.tdNum, color: lastDemand != null ? '#333' : '#aaa' }}>{lastDemand ?? '—'}</td>
                          <td style={{ ...styles.tdNum, color: lastDemand != null ? '#333' : '#aaa' }}>{lastDemand != null ? lastDemand - cat.supply : '—'}</td>
                          <td style={styles.tdNum}>{fmtPrice(cat.startingPrice)}</td>
                          <td style={{ ...styles.tdNum, color: p ? (currentRound ? '#c62828' : '#333') : '#aaa', fontWeight: p ? 600 : 400 }}>{fmtPrice(p?.price)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', display: 'flex', gap: '1.5rem' }}>
                  <span>Activity Requirement: {auctionDetail.rho}</span>
                  <span>Activity Tolerance: {auctionDetail.activityTolerance ?? 0} pt</span>
                  <span>Price Increment: {auctionDetail.priceIncrement}%</span>
                </div>
                {(auctionDetail.caps ?? []).length > 0 && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.4rem' }}>Caps</div>
                    {(auctionDetail.caps ?? []).map((cap: any) => {
                      const nonzeroTerms = (cap.terms ?? []).filter((t: any) => t.coefficient !== 0);
                      const formula = nonzeroTerms.map((t: any) => {
                        const cat = auctionDetail.categories.find((c: any) => c.id === t.categoryId);
                        return `${cat?.name ?? t.categoryId} × ${t.coefficient}`;
                      }).join(' + ');
                      const isDegenerate = nonzeroTerms.length === 0;
                      return (
                        <div key={cap.id} style={{ marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                          <strong>{cap.name}</strong> — limit: {cap.limit}
                          <span style={{ color: isDegenerate ? '#c62828' : '#888', marginLeft: '0.75rem' }}>
                            {isDegenerate ? '⚠ no active terms (degenerate cap)' : formula}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Auction ID, for handing to bidders who bid through the external API
                    (Excel/WinHttp tooling — see docs/25). Bidders can also read it for
                    themselves from the integration panel on their own screen; this copy is
                    here so the auctioneer can help one who cannot find it.
                    Last in the section, after Caps, so it stays out of the way of the
                    auction parameters an auctioneer actually reads each round.
                    Rendered as a readonly input so one click selects the whole GUID and
                    Ctrl+C works even where the clipboard API is unavailable. */}
                <div style={{ marginTop: '0.9rem', paddingTop: '0.75rem', borderTop: '1px solid #eee', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ color: '#555', whiteSpace: 'nowrap' }}>Auction ID:</span>
                  <input
                    readOnly
                    value={auctionDetail.id}
                    onFocus={e => e.currentTarget.select()}
                    onClick={e => e.currentTarget.select()}
                    style={{ fontFamily: 'monospace', fontSize: '0.8rem', padding: '0.25rem 0.4rem', border: '1px solid #ddd', borderRadius: '4px', width: '23rem', background: '#fafafa', color: '#333' }}
                  />
                  <button
                    style={styles.csvBtn}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(auctionDetail.id);
                        setCopiedId(true);
                        setTimeout(() => setCopiedId(false), 1500);
                      } catch {
                        // Clipboard API needs a secure context and can be blocked by policy.
                        // The input is already selectable, so fall back to telling them that.
                        setCopyFailed(true);
                        setTimeout(() => setCopyFailed(false), 2500);
                      }
                    }}
                  >
                    {copiedId ? 'Copied' : 'Copy'}
                  </button>
                  {copyFailed && (
                    <span style={{ color: '#c62828', fontSize: '0.8rem' }}>
                      Copy blocked — click the box and press Ctrl+C
                    </span>
                  )}
                  <span style={{ color: '#888', fontSize: '0.8rem' }}>
                    for bidders using the Excel/API integration
                  </span>
                </div>
              </div>

              {/* Activity Log */}
              <div style={{ flex: 2, minWidth: 0, position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.4rem', flexShrink: 0 }}>Activity Log</div>
                  <div style={{ flex: 1, overflowY: 'auto', minHeight: 0,
                                border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff',
                                padding: '0.4rem 0.6rem', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    {(bidLogs[selected.id] ?? []).length === 0 ? (
                      <p style={{ ...styles.meta, color: '#aaa', margin: 0 }}>No activity yet this session.</p>
                    ) : (
                      (bidLogs[selected.id] ?? []).map((entry, i) => (
                        <div key={i} style={{ lineHeight: 1.7,
                          color: entry.kind === 'bid' ? '#333' : entry.kind === 'round_opened' ? '#1e7e34' : '#c62828',
                          fontWeight: entry.kind !== 'bid' ? 600 : 400 }}>
                          <span style={{ color: '#aaa' }}>{new Date(entry.time).toLocaleTimeString()}</span>
                          {' '}<span style={{ color: '#3949ab' }}>R{entry.roundNumber}</span>{' '}
                          {entry.kind === 'bid' ? entry.bidderName : entry.kind === 'round_opened' ? '— round opened' : '— round closed'}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>}
          </div>

          {closedRounds.length > 0 && (
            <div style={styles.roundBox}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showHistory ? '0.75rem' : 0 }}>
                <button style={styles.collapseBtn} onClick={() => setShowHistory(h => !h)}>
                  <span style={styles.chevron(showHistory)}>▶</span> History
                </button>
                {showHistory && <button style={styles.csvBtn} onClick={downloadAggregateCsv}>Download Aggregate CSV</button>}
              </div>

              {showHistory && <><h5 style={styles.subHeading}>Price &amp; Demand by Round</h5>
              <div style={styles.scrollX}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.thNum}>Round</th>
                      {auctionDetail.categories.map((cat: any) => <th key={`${cat.id}-p`} style={styles.thNum}>{cat.name} Price</th>)}
                      {auctionDetail.categories.map((cat: any) => <th key={`${cat.id}-d`} style={styles.thNum}>{cat.name} Demand</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRounds.map((r: any) => (
                      <tr key={r.id}>
                        <td style={styles.tdNum}>{r.roundNumber}{r.status === 'ACTIVE' ? ' *' : ''}</td>
                        {auctionDetail.categories.map((cat: any) => {
                          const p = r.categoryPrices.find((x: any) => x.categoryId === cat.id);
                          return <td key={`${cat.id}-p`} style={styles.tdNum}>{fmtPrice(p?.price)}</td>;
                        })}
                        {auctionDetail.categories.map((cat: any) => {
                          const p = r.categoryPrices.find((x: any) => x.categoryId === cat.id);
                          return <td key={`${cat.id}-d`} style={styles.tdNum}>{r.status === 'CLOSED' ? (p?.aggregateDemand ?? '—') : '—'}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                <h5 style={{ ...styles.subHeading, margin: 0 }}>Bidder Bids by Round</h5>
                <button style={styles.csvBtn} onClick={downloadBidderCsv}>Download Bidder CSV</button>
              </div>
              <div style={styles.scrollX}>
                {(() => {
                  const bidders = getSortedBidders();
                  return (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.thNum}>Round</th>
                          {auctionDetail.categories.map((cat: any) => <th key={`${cat.id}-p`} style={styles.thNum}>{cat.name} Price</th>)}
                          {bidders.flatMap(([bidderId, bidderName]) =>
                            auctionDetail.categories.map((cat: any) => <th key={`${bidderId}-${cat.id}`} style={styles.thNum}>{cat.name} {bidderName}</th>)
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {closedRounds.map((r: any) => (
                          <tr key={r.id}>
                            <td style={styles.tdNum}>{r.roundNumber}</td>
                            {auctionDetail.categories.map((cat: any) => {
                              const p = r.categoryPrices.find((x: any) => x.categoryId === cat.id);
                              return <td key={`${cat.id}-p`} style={styles.tdNum}>{fmtPrice(p?.price)}</td>;
                            })}
                            {bidders.flatMap(([bidderId]) =>
                              auctionDetail.categories.map((cat: any) => {
                                const bid = (allRoundBids[r.id] ?? []).find((b: any) => b.bidderId === bidderId);
                                const qty = bid?.items.find((i: any) => i.categoryId === cat.id)?.quantity ?? 0;
                                return <td key={`${bidderId}-${cat.id}`} style={styles.tdNum}>{qty}</td>;
                              })
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              {currentRound && <p style={{ ...styles.meta, marginTop: '0.5rem' }}>* Active round — demand not yet available</p>}

              {/* Exit bid history across all rounds */}
              {(() => {
                const allExitBids = closedRounds.flatMap((r: any) =>
                  (r.exitBidHistory ?? []).map((eb: any) => ({ ...eb, _roundNumber: r.roundNumber }))
                );
                if (allExitBids.length === 0) return null;
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                      <h5 style={{ ...styles.subHeading, margin: 0 }}>Exit Bids by Round</h5>
                      <button style={styles.csvBtn} onClick={downloadExitBidCsv}>Download Exit Bids CSV</button>
                    </div>
                    <div style={styles.scrollX}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.thNum}>Round</th>
                            <th style={styles.th}>Bidder</th>
                            <th style={styles.th}>Category</th>
                            <th style={styles.thNum}>From</th>
                            <th style={styles.thNum}>+Qty</th>
                            <th style={styles.thNum}>To</th>
                            <th style={styles.thNum}>Bid Price</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.thNum}>Src Round</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allExitBids.map((eb: any, i: number) => (
                            <tr key={i}>
                              <td style={styles.tdNum}>{eb._roundNumber}</td>
                              <td style={styles.td}>{eb.bidderName}</td>
                              <td style={styles.td}>{eb.categoryName}</td>
                              <td style={styles.tdNum}>{eb.fromQuantity}</td>
                              <td style={styles.tdNum}>{eb.incrementQuantity}</td>
                              <td style={styles.tdNum}>{eb.toQuantity}</td>
                              <td style={styles.tdNum}>{fmtPrice(eb.bidPrice)}</td>
                              <td style={{ ...styles.td, fontSize: '0.8rem', color: eb.roundState?.isRetained ? '#1e7e34' : '#c62828' }}>
                                {eb.roundState?.status}
                              </td>
                              <td style={styles.tdNum}>{eb.sourceRoundNumber}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
              </>}
            </div>
          )}

          {lastClosedRound && roundBids.length > 0 && (
            <div style={styles.roundBox}>
              <button style={styles.collapseBtn} onClick={() => setShowRoundResults(h => !h)}>
                <span style={styles.chevron(showRoundResults)}>▶</span> Round {lastClosedRound.roundNumber} Results
              </button>
              {showRoundResults && <>
                <table style={{ ...styles.table, marginTop: '0.75rem' }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Bidder</th>
                      {auctionDetail.categories.map((cat: any) => <th key={cat.id} style={styles.thNum}>{cat.name}</th>)}
                      <th style={styles.thNum}>Eligibility</th>
                      <th style={styles.thNum}>Activity</th>
                      <th style={styles.thNum}>Bid Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...roundBids].sort((a, b) => a.bidderName.localeCompare(b.bidderName, undefined, { numeric: true })).map((bid: any) => {
                      const activity = auctionDetail.categories.reduce((sum: number, cat: any) => {
                        const qty = bid.items.find((i: any) => i.categoryId === cat.id)?.quantity ?? 0;
                        return sum + qty * cat.activityPoints;
                      }, 0);
                      const bidAmount = auctionDetail.categories.reduce((sum: number, cat: any) => {
                        const qty = bid.items.find((i: any) => i.categoryId === cat.id)?.quantity ?? 0;
                        const price = lastClosedRound.categoryPrices.find((p: any) => p.categoryId === cat.id)?.price ?? 0;
                        return sum + qty * price;
                      }, 0);
                      return (
                        <tr key={bid.bidderId}>
                          <td style={styles.td}>{bid.bidderName}</td>
                          {auctionDetail.categories.map((cat: any) => {
                            const item = bid.items.find((i: any) => i.categoryId === cat.id);
                            return <td key={cat.id} style={styles.tdNum}>{item?.quantity ?? 0}</td>;
                          })}
                          <td style={styles.tdNum}>{(() => { const e = lastClosedRound.eligibilities?.find((e: any) => e.userId === bid.bidderId); return e?.openingEligibility ?? e?.eligibility ?? '—'; })()}</td>
                          <td style={styles.tdNum}>{activity}</td>
                          <td style={styles.tdNum}>{bidAmount.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                    {(() => {
                      const totalDemand = auctionDetail.categories.reduce((acc: Record<string, number>, cat: any) => {
                        acc[cat.id] = roundBids.reduce((s: number, bid: any) => s + (bid.items.find((i: any) => i.categoryId === cat.id)?.quantity ?? 0), 0);
                        return acc;
                      }, {});
                      const totalEligibility = roundBids.reduce((s: number, bid: any) => {
                        const e = lastClosedRound.eligibilities?.find((e: any) => e.userId === bid.bidderId);
                        const val = e?.openingEligibility ?? e?.eligibility;
                        return s + (typeof val === 'number' ? val : 0);
                      }, 0);
                      const totalActivity = roundBids.reduce((s: number, bid: any) =>
                        s + auctionDetail.categories.reduce((ss: number, cat: any) => {
                          const qty = bid.items.find((i: any) => i.categoryId === cat.id)?.quantity ?? 0;
                          return ss + qty * cat.activityPoints;
                        }, 0), 0);
                      const totalBidAmount = roundBids.reduce((s: number, bid: any) =>
                        s + auctionDetail.categories.reduce((ss: number, cat: any) => {
                          const qty = bid.items.find((i: any) => i.categoryId === cat.id)?.quantity ?? 0;
                          const price = lastClosedRound.categoryPrices.find((p: any) => p.categoryId === cat.id)?.price ?? 0;
                          return ss + qty * price;
                        }, 0), 0);
                      return (
                        <>
                          <tr style={styles.totalRow}>
                            <td style={styles.td}><strong>Total</strong></td>
                            {auctionDetail.categories.map((cat: any) => (
                              <td key={cat.id} style={styles.tdNum}><strong>{totalDemand[cat.id]}</strong></td>
                            ))}
                            <td style={styles.tdNum}><strong>{totalEligibility}</strong></td>
                            <td style={styles.tdNum}><strong>{totalActivity}</strong></td>
                            <td style={styles.tdNum}><strong>{totalBidAmount.toLocaleString()}</strong></td>
                          </tr>
                          <tr style={styles.totalRow}>
                            <td style={styles.td}><strong>Excess</strong></td>
                            {auctionDetail.categories.map((cat: any) => (
                              <td key={cat.id} style={styles.tdNum}><strong>{totalDemand[cat.id] - cat.supply}</strong></td>
                            ))}
                            <td style={styles.tdNum}></td>
                            <td style={styles.tdNum}></td>
                            <td style={styles.tdNum}></td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>

                {/* Exit bid history for this round */}
                {lastClosedRound.exitBidHistory?.length > 0 && (
                  <>
                    <h5 style={{ ...styles.subHeading, marginTop: '1rem' }}>Exit Bid Activity This Round</h5>
                    <div style={styles.scrollX}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Bidder</th>
                            <th style={styles.th}>Category</th>
                            <th style={styles.thNum}>From</th>
                            <th style={styles.thNum}>+Qty</th>
                            <th style={styles.thNum}>To</th>
                            <th style={styles.thNum}>Bid Price</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>From Round</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastClosedRound.exitBidHistory.map((eb: any) => (
                            <tr key={`${eb.id}-${eb.roundState?.roundId}`}>
                              <td style={styles.td}>{eb.bidderName}</td>
                              <td style={styles.td}>{eb.categoryName}</td>
                              <td style={styles.tdNum}>{eb.fromQuantity}</td>
                              <td style={styles.tdNum}>{eb.incrementQuantity}</td>
                              <td style={styles.tdNum}>{eb.toQuantity}</td>
                              <td style={styles.tdNum}>{fmtPrice(eb.bidPrice)}</td>
                              <td style={{ ...styles.td, fontSize: '0.8rem', color: eb.roundState?.isRetained ? '#1e7e34' : '#c62828' }}>{eb.roundState?.status}</td>
                              <td style={styles.tdNum}>{eb.sourceRoundNumber}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>}
            </div>
          )}

          {/* Bidbots panel */}
          <div style={styles.roundBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button style={styles.collapseBtn} onClick={() => setShowBidbots(h => !h)}>
                <span style={styles.chevron(showBidbots)}>▶</span> Bidbots
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={styles.meta}>Bots:</span>
                <button
                  style={botEnabled ? styles.botOnBtn : styles.botOffBtn}
                  onClick={() => toggleAuctionBots(!botEnabled)}
                >
                  {botEnabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>

            {showBidbots && (
              <>
                {/* Assignments */}
                <div style={{ marginTop: '1rem' }}>
                  <h5 style={styles.subHeading}>Assignments</h5>
                  {botAssignments.length > 0 && (
                    <div style={{ ...styles.scrollX, marginBottom: '0.75rem' }}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Bidder</th>
                            <th style={styles.th}>Strategy</th>
                            <th style={styles.th}>Parameters</th>
                            <th style={styles.th}>Enabled</th>
                            <th style={styles.th}>Last Run</th>
                            <th style={styles.th}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {botAssignments.map((a: any) => (
                            <tr key={a.id}>
                              <td style={styles.td}>{a.bidderName}</td>
                              <td style={{ ...styles.td, fontSize: '0.8rem' }}>{a.strategyFilename}</td>
                              <td style={{ ...styles.td, fontSize: '0.8rem' }}>{a.parameterFilename}</td>
                              <td style={styles.td}>
                                <button
                                  style={a.enabled ? styles.botOnBtn : styles.botOffBtn}
                                  onClick={() => toggleAssignment(a.id, !a.enabled)}
                                >{a.enabled ? 'On' : 'Off'}</button>
                              </td>
                              <td style={{ ...styles.td, fontSize: '0.8rem' }}>
                                {a.lastExecutionStatus
                                  ? <span style={{ color: a.lastExecutionStatus === 'SUCCESS' ? '#2e7d32' : '#c62828' }}>
                                      {a.lastExecutionStatus}
                                      {a.lastExecutionAt ? ` — ${new Date(a.lastExecutionAt).toLocaleString()}` : ''}
                                    </span>
                                  : '—'}
                              </td>
                              <td style={styles.td}>
                                <button style={styles.deleteBtn} onClick={() => deleteAssignment(a.id, a.bidderName)}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* New assignment form */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' as const, alignItems: 'flex-end' }}>
                    <div>
                      <p style={{ ...styles.meta, marginBottom: '0.2rem' }}>Bidder</p>
                      <select style={styles.botSelect} value={newAssignBidderId} onChange={e => setNewAssignBidderId(e.target.value)}>
                        <option value="">— select —</option>
                        {allBidders.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <p style={{ ...styles.meta, marginBottom: '0.2rem' }}>Strategy</p>
                      <select style={styles.botSelect} value={newAssignStrategyId} onChange={e => setNewAssignStrategyId(e.target.value)}>
                        <option value="">— select —</option>
                        {botArtifacts.filter((a: any) => a.artifactType === 'STRATEGY').map((a: any) => (
                          <option key={a.id} value={a.id}>{a.originalFilename}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p style={{ ...styles.meta, marginBottom: '0.2rem' }}>Parameters</p>
                      <select style={styles.botSelect} value={newAssignParamId} onChange={e => setNewAssignParamId(e.target.value)}>
                        <option value="">— select —</option>
                        {botArtifacts.filter((a: any) => a.artifactType === 'PARAMETERS').map((a: any) => (
                          <option key={a.id} value={a.id}>{a.originalFilename}</option>
                        ))}
                      </select>
                    </div>
                    <button style={styles.openBtn} onClick={createAssignment}>Save Assignment</button>
                  </div>
                  {assignError && <p style={{ color: '#c62828', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{assignError}</p>}
                </div>

                {/* Execution history */}
                {botExecutions.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <button style={styles.collapseBtn} onClick={() => setShowBotExecutions(h => !h)}>
                      <span style={styles.chevron(showBotExecutions)}>▶</span>
                      {` Execution History (${botExecutions.length})`}
                    </button>
                    {showBotExecutions && (
                      <div style={{ ...styles.scrollX, marginTop: '0.5rem' }}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              <th style={styles.th}>Bidder</th>
                              <th style={styles.thNum}>Round</th>
                              <th style={styles.th}>Status</th>
                              <th style={styles.th}>Started</th>
                              <th style={styles.th}>Finished</th>
                              <th style={styles.th}>Failure</th>
                            </tr>
                          </thead>
                          <tbody>
                            {botExecutions.map((e: any) => {
                              const assign = botAssignments.find((a: any) => a.id === e.assignmentId);
                              const round = auctionDetail.rounds.find((r: any) => r.id === e.roundId);
                              return (
                                <tr key={e.id}>
                                  <td style={styles.td}>{assign?.bidderName ?? e.bidderId}</td>
                                  <td style={styles.tdNum}>{round?.roundNumber ?? '—'}</td>
                                  <td style={{ ...styles.td, fontWeight: 600, color: e.status === 'SUCCESS' ? '#2e7d32' : '#c62828' }}>
                                    {e.status}
                                  </td>
                                  <td style={{ ...styles.td, fontSize: '0.8rem' }}>
                                    {e.startedAt ? new Date(e.startedAt).toLocaleString() : '—'}
                                  </td>
                                  <td style={{ ...styles.td, fontSize: '0.8rem' }}>
                                    {e.finishedAt ? new Date(e.finishedAt).toLocaleString() : '—'}
                                  </td>
                                  <td style={{ ...styles.td, fontSize: '0.8rem', color: '#c62828' }}>
                                    {e.failureReason ?? ''}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {auctionDetail.status === 'CLOSED' && (
            <div style={{ ...styles.roundBox, borderColor: '#1e7e34', background: '#e6f4ea' }}>
              <h4 style={{ color: '#1e7e34', marginTop: 0 }}>
                Auction Closed — Final Results
                {finalResults && ` | Round ${finalResults.finalRound} | Total Revenue: ${finalResults.totalRevenue.toLocaleString()}`}
              </h4>
              {!finalResults ? (
                <p style={styles.meta}>Results not yet available.</p>
              ) : (
                <>
                  <ResultsByBidder results={finalResults} />
                  <h5 style={{ ...styles.subHeading, color: '#1e7e34', fontSize: '1rem' }}>Results by Band</h5>
                  {finalResults.categories.map((cat: any) => {
                    const sortedAllocs = [...cat.allocations].sort((a: any, b: any) =>
                      a.bidderName.localeCompare(b.bidderName, undefined, { numeric: true })
                    );
                    const totalAllocated = cat.totalClockAllocated + cat.totalExtensionAllocated;
                    const unallocated = cat.supply - totalAllocated;
                    const catTotalPayment = sortedAllocs.reduce((s: number, a: any) => s + a.totalPayment, 0);
                    return (
                      <div key={cat.categoryId} style={{ marginBottom: '1.25rem' }}>
                        <h5 style={{ ...styles.subHeading, marginBottom: '0.4rem' }}>
                          {cat.categoryName} — Supply: {cat.supply} | Clock Price: {cat.clockPrice.toLocaleString()} | Residual: {cat.residualSupply}
                        </h5>
                        <div style={styles.scrollX}>
                          <table style={styles.table}>
                            <thead>
                              <tr>
                                <th style={styles.th}>Bidder</th>
                                <th style={styles.thNum}>Clock Qty</th>
                                <th style={styles.thNum}>Ext Qty</th>
                                <th style={styles.thNum}>Total Qty</th>
                                <th style={styles.thNum}>Clock Payment</th>
                                <th style={styles.thNum}>Ext Revenue</th>
                                <th style={styles.thNum}>Total Payment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedAllocs.map((a: any) => (
                                <tr key={a.bidderId}>
                                  <td style={styles.td}>{a.bidderName}</td>
                                  <td style={styles.tdNum}>{a.clockQuantity}</td>
                                  <td style={styles.tdNum}>{a.extensionQuantity}</td>
                                  <td style={styles.tdNum}>{a.totalQuantity}</td>
                                  <td style={styles.tdNum}>{a.clockPayment.toLocaleString()}</td>
                                  <td style={styles.tdNum}>{a.extensionRevenue.toLocaleString()}</td>
                                  <td style={styles.tdNum}>{a.totalPayment.toLocaleString()}</td>
                                </tr>
                              ))}
                              <tr style={styles.totalRow}>
                                <td style={styles.td}><strong>Total Allocated</strong></td>
                                <td style={styles.tdNum}><strong>{cat.totalClockAllocated}</strong></td>
                                <td style={styles.tdNum}><strong>{cat.totalExtensionAllocated}</strong></td>
                                <td style={styles.tdNum}><strong>{totalAllocated}</strong></td>
                                <td style={styles.tdNum}><strong>{(cat.totalClockAllocated * cat.clockPrice).toLocaleString()}</strong></td>
                                <td style={styles.tdNum}></td>
                                <td style={styles.tdNum}><strong>{catTotalPayment.toLocaleString()}</strong></td>
                              </tr>
                              <tr style={{ background: '#f1f8e9' }}>
                                <td style={styles.td}><strong>Unallocated</strong></td>
                                <td style={styles.tdNum}></td>
                                <td style={styles.tdNum}></td>
                                <td style={{ ...styles.tdNum, color: unallocated > 0 ? '#e65100' : 'inherit' }}>
                                  <strong>{unallocated}</strong>
                                </td>
                                <td style={styles.tdNum}>—</td>
                                <td style={styles.tdNum}>—</td>
                                <td style={styles.tdNum}>—</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ ...styles.totalRow, padding: '0.5rem 0.6rem', borderTop: '2px solid #ccc', marginTop: '0.5rem' }}>
                    <strong>Grand Total Revenue: {finalResults.totalRevenue.toLocaleString()}</strong>
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        {showRoundForm && auctionDetail && (
          <div style={styles.modalOverlay} onClick={() => setShowRoundForm(false)}>
            <div style={styles.modalCardWide} onClick={e => e.stopPropagation()}>
              <OpenRoundForm
                auction={auctionDetail}
                previousRound={lastClosedRound}
                onOpened={() => { setShowRoundForm(false); loadAuctionDetail(selected.id); }}
                onCancel={() => setShowRoundForm(false)}
              />
            </div>
          </div>
        )}

        {cloningAuction && (
          <div style={styles.modalOverlay} onClick={() => setCloningAuction(null)}>
            <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
              <h4 style={{ margin: '0 0 1rem', color: '#6a1b9a' }}>Clone: {cloningAuction.title}</h4>
              <form onSubmit={handleCloneSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={styles.meta}>New title</label>
                <input style={styles.cloneInput} value={cloneTitle} onChange={e => setCloneTitle(e.target.value)} required />
                <label style={styles.meta}>Copy through round (0 = setup only, max {cloningAuction.closedCount})</label>
                <input style={{ ...styles.cloneInput, width: '100px' }} type="number" min={0} max={cloningAuction.closedCount}
                  value={cloneRound} onChange={e => setCloneRound(parseInt(e.target.value) || 0)} required />
                <label style={styles.meta}>Activity requirement (ρ)</label>
                <input style={{ ...styles.cloneInput, width: '100px' }} type="number" min={0.1} max={1} step="0.01"
                  value={cloneRho} onChange={e => setCloneRho(e.target.value)} required />
                <label style={styles.meta}>Price increment (%)</label>
                <input style={{ ...styles.cloneInput, width: '100px' }} type="number" min={0} step="1"
                  value={clonePriceIncrement} onChange={e => setClonePriceIncrement(e.target.value)} required />
                <label style={styles.meta}>Activity tolerance (points)</label>
                <input style={{ ...styles.cloneInput, width: '100px' }} type="number" min={0} max={10} step="1"
                  value={cloneActivityTolerance} onChange={e => setCloneActivityTolerance(e.target.value)} required />
                {cloneError && <p style={{ color: 'red', fontSize: '0.875rem', margin: 0 }}>{cloneError}</p>}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button type="button" style={{ ...styles.backBtn, marginBottom: 0, padding: '0.5rem 1rem' }} onClick={() => setCloningAuction(null)}>Cancel</button>
                  <button type="submit" style={styles.openBtn}>Create Clone</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  async function handleCloneSubmit(e: React.FormEvent) {
    e.preventDefault();
    setCloneError('');
    try {
      await api.post(`/auctions/${cloningAuction.id}/clone`, {
        title: cloneTitle,
        targetRound: cloneRound,
        rho: parseFloat(cloneRho),
        priceIncrement: parseFloat(clonePriceIncrement),
        activityTolerance: parseInt(cloneActivityTolerance),
      });
      setCloningAuction(null);
      onRefresh();
    } catch (err: any) {
      setCloneError(err.response?.data?.detail ?? 'Clone failed');
    }
  }

  return (
    <div>
      {auctions.length === 0 && <p style={{ color: '#888' }}>No auctions yet. Create one to get started.</p>}
      <div style={styles.grid}>
        {[...auctions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(auction => {
          const rs = getAuctionRoundStatus(auction);
          return (
            <div key={auction.id} style={styles.card} onClick={() => setSelected(auction)}>
              <h4 style={{ margin: '0 0 0.5rem' }}>{auction.title}</h4>
              <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', background: rs.bg, color: rs.color }}>{rs.text}</span>
              <p style={styles.meta}>{auction.categories.length} categories &bull; {auction.rounds.length} rounds</p>
            </div>
          );
        })}
      </div>

      {/* Global Artifact Library */}
      <div style={styles.roundBox}>
        <button style={styles.collapseBtn} onClick={() => setShowArtifactLibrary(h => !h)}>
          <span style={styles.chevron(showArtifactLibrary)}>▶</span>
          {` Artifact Library (${botArtifacts.length})`}
        </button>
        {showArtifactLibrary && (
          <>
            {/* Upload */}
            <div style={{ marginTop: '1rem' }}>
              <h5 style={styles.subHeading}>Upload Artifacts</h5>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' as const }}>
                <div>
                  <p style={{ ...styles.meta, marginBottom: '0.25rem' }}>Strategy script (.py)</p>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input key={`strategy-${botFileKey}`} type="file" accept=".py"
                      onChange={e => setBotStrategyFile(e.target.files?.[0] ?? null)} />
                    <button style={styles.csvBtn} onClick={() => uploadArtifact(botStrategyFile, 'STRATEGY')}>Upload</button>
                  </div>
                </div>
                <div>
                  <p style={{ ...styles.meta, marginBottom: '0.25rem' }}>Parameters (.json)</p>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input key={`param-${botFileKey}`} type="file" accept=".json"
                      onChange={e => setBotParamFile(e.target.files?.[0] ?? null)} />
                    <button style={styles.csvBtn} onClick={() => uploadArtifact(botParamFile, 'PARAMETERS')}>Upload</button>
                  </div>
                </div>
              </div>
              {botUploadMsg && (
                <p style={{ fontSize: '0.85rem', color: botUploadMsg.includes('failed') ? '#c62828' : '#2e7d32', margin: '0.5rem 0 0' }}>
                  {botUploadMsg}
                </p>
              )}
            </div>

            {/* Artifact table */}
            <div style={{ marginTop: '1rem' }}>
              {botArtifacts.length === 0
                ? <p style={styles.meta}>No artifacts uploaded yet.</p>
                : (
                  <div style={styles.scrollX}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Type</th>
                          <th style={styles.th}>Filename</th>
                          <th style={styles.th}>Uploaded</th>
                          <th style={styles.th}>By</th>
                          <th style={styles.th}>Hash</th>
                          <th style={styles.th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {botArtifacts.map((a: any) => (
                          <tr key={a.id}>
                            <td style={{ ...styles.td, fontSize: '0.8rem' }}>
                              <span style={{ background: a.artifactType === 'STRATEGY' ? '#e8eaf6' : '#e8f5e9', padding: '0.1rem 0.4rem', borderRadius: 4 }}>
                                {a.artifactType}
                              </span>
                            </td>
                            <td style={{ ...styles.td, fontSize: '0.8rem' }}>{a.originalFilename}</td>
                            <td style={{ ...styles.td, fontSize: '0.8rem' }}>{new Date(a.uploadedAt).toLocaleString()}</td>
                            <td style={{ ...styles.td, fontSize: '0.8rem' }}>{a.uploaderName}</td>
                            <td style={{ ...styles.td, fontSize: '0.75rem', fontFamily: 'monospace' }}>{a.contentHash.slice(0, 10)}…</td>
                            <td style={styles.td}>
                              <button style={styles.deleteBtn} onClick={() => deleteArtifact(a.id, a.originalFilename)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
              {artifactDeleteMsg && (
                <p style={{ color: '#c62828', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{artifactDeleteMsg}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles: any = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' },
  card: { background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', cursor: 'pointer' },
  detailCard: { background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  badge: (status: string) => ({
    display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold',
    background: status === 'ACTIVE' ? '#e6f4ea' : status === 'CLOSED' ? '#fce8e6' : '#e8eaf6',
    color: status === 'ACTIVE' ? '#1e7e34' : status === 'CLOSED' ? '#c62828' : '#3949ab',
  }),
  meta: { color: '#888', fontSize: '0.85rem', margin: '0.25rem 0' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', marginBottom: '0.5rem' },
  th: { padding: '0.4rem 0.6rem', borderBottom: '2px solid #ddd', textAlign: 'left' as const, fontWeight: 600, color: '#555' },
  thNum: { padding: '0.4rem 0.6rem', borderBottom: '2px solid #ddd', textAlign: 'right' as const, fontWeight: 600, color: '#555' },
  td: { padding: '0.35rem 0.6rem', borderBottom: '1px solid #f0f0f0' },
  tdNum: { padding: '0.35rem 0.6rem', borderBottom: '1px solid #f0f0f0', textAlign: 'right' as const },
  roundBox: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '1rem', marginTop: '1rem', background: '#fafafa' },
  backBtn: { marginBottom: '1rem', padding: '0.4rem 0.8rem', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' },
  closeBtn: { marginTop: '0.75rem', padding: '0.5rem 1rem', background: '#e53935', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  deleteBtn: { padding: '0.4rem 0.9rem', background: '#e53935', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 },
  timerBadge: { fontSize: '1.1rem', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' },
  openBtn: { padding: '0.5rem 1rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  openBtnDisabled: { padding: '0.5rem 1rem', background: '#b0bec5', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'not-allowed', opacity: 0.6 },
  processBtn: { padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  processBtnDisabled: { padding: '0.5rem 1rem', background: '#b0bec5', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'not-allowed', opacity: 0.6 },
  auditBtn: { padding: '0.5rem 1rem', background: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  auditBtnDisabled: { padding: '0.5rem 1rem', background: '#b0bec5', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'not-allowed', opacity: 0.6 },
  csvBtn: { padding: '0.4rem 0.8rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
  cloneBtn: { padding: '0.4rem 0.9rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 },
  cloneInput: { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem', width: '100%', boxSizing: 'border-box' as const },
  modalOverlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: 'white', borderRadius: '8px', padding: '1.5rem', width: '420px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' },
  modalCardWide: { background: 'white', borderRadius: '8px', padding: '1.5rem', width: '540px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' as const, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' },
  collapseBtn: { background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', padding: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  chevron: (open: boolean) => ({ display: 'inline-block', transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: '0.75rem' }),
  totalRow: { borderTop: '2px solid #ddd', background: '#fafafa' },
  subHeading: { margin: '0 0 0.5rem', fontWeight: 600, color: '#444', fontSize: '0.9rem' },
  scrollX: { overflowX: 'auto' as const },
  botOnBtn: { padding: '0.25rem 0.6rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 },
  botOffBtn: { padding: '0.25rem 0.6rem', background: '#b0bec5', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 },
  botSelect: { padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.875rem', minWidth: '160px' },
};
